import React, { useEffect } from "react";
import { Button, notification } from "antd";
import "styles/versionUpdateNotice.scss";

const CHECK_INTERVAL_MS = 3 * 60 * 1000;
// Updating is mandatory: once a new version is detected the user gets this
// long to finish what they are doing before the app refreshes itself.
const FORCED_RELOAD_DELAY_MS = 1 * 60 * 1000;
// CRA emits one hashed entry script per build; the hash changes on every deploy,
// so it doubles as a version identifier without any build-time tooling.
const MAIN_SCRIPT_REGEX = /\/static\/js\/main\.\w+\.js/;

const getLoadedMainScript = () => {
  const script = document.querySelector('script[src*="/static/js/main."]');
  return script?.getAttribute("src")?.match(MAIN_SCRIPT_REGEX)?.[0] ?? null;
};

// Reads the "dark_theme" localStorage key (see constants/profile DARK_THEME)
// directly: this hook loads with App.jsx before anything else, and importing
// constants/profile here pulls it into a circular-import chain with
// helpers/generalHelper and api/axios that crashes the bundle at startup.
const isDarkTheme = () => {
  try {
    return JSON.parse(localStorage.getItem("dark_theme")) !== false;
  } catch {
    return true;
  }
};

const formatRemaining = (ms) => {
  const totalSeconds = Math.max(Math.ceil(ms / 1000), 0);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

/**
 * Detects when a new version of the app has been deployed while this tab is
 * open. Shows a mandatory (non-dismissible) themed notice with a countdown,
 * then refreshes automatically so no user can keep running an old version.
 */
export const useVersionCheck = () => {
  useEffect(() => {
    const loadedScript = getLoadedMainScript();
    // Dev-server bundles are not hashed, so there is nothing to compare.
    if (!loadedScript) return undefined;

    let countdownId = null;

    const showUpdateNotice = () => {
      if (countdownId !== null) return;

      // Nobody is looking at the tab, so the refresh is invisible — skip the
      // countdown entirely.
      if (document.visibilityState === "hidden") {
        window.location.reload();
        return;
      }

      // Default is dark (matches GlobalContext's initial state).
      const isDark = isDarkTheme();
      const deadline = Date.now() + FORCED_RELOAD_DELAY_MS;

      const render = () => {
        const remaining = deadline - Date.now();

        if (remaining <= 0) {
          clearInterval(countdownId);
          window.location.reload();
          return;
        }

        // Inline background/color so antd's default white notice cannot win the
        // cascade (same palette as .ant-modal-dark / Additional Data).
        notification.info({
          key: "new-version-available",
          className: `version-update-notice version-update-notice--${
            isDark ? "dark" : "light"
          }`,
          style: isDark
            ? {
                background: "var(--color-black-1)",
                border: "1px solid var(--color-gray-2)",
                color: "var(--color-white-1)",
              }
            : {
                background: "var(--color-white-1)",
                border: "1px solid var(--color-gray-1)",
                color: "var(--color-black-1)",
              },
          message: "Update required",
          description: (
            <div>
              A new version of Aratus has been released. You will stay signed
              in and nothing will be lost after the refresh.
              <div className="version-update-notice__countdown">
                Refreshing automatically in{" "}
                <strong>{formatRemaining(remaining)}</strong>
              </div>
            </div>
          ),
          placement: "topRight",
          duration: 0,
          closeIcon: <span />,
          btn: (
            <Button
              type="primary"
              size="small"
              onClick={() => window.location.reload()}
            >
              Refresh now
            </Button>
          ),
        });
      };

      render();
      // Recomputed from the deadline each tick, so background-tab throttling
      // only delays the display, never the actual reload time.
      countdownId = setInterval(render, 1000);
    };

    const checkForNewVersion = async () => {
      try {
        const response = await fetch("/index.html", { cache: "no-store" });
        if (!response.ok) return;
        const html = await response.text();
        const latestScript = html.match(MAIN_SCRIPT_REGEX)?.[0];
        if (latestScript && latestScript !== loadedScript) {
          showUpdateNotice();
        }
      } catch {
        // Offline or flaky network — try again on the next check.
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForNewVersion();
      } else if (countdownId !== null) {
        // The user walked away while the countdown was running — refresh now,
        // while they cannot see the interruption.
        window.location.reload();
      }
    };

    const intervalId = setInterval(checkForNewVersion, CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      if (countdownId !== null) clearInterval(countdownId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
};
